import React, { useState, useEffect, useCallback } from 'react';
import { DashboardSectionSkeleton, InlineSpinner } from './common/loading';

import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Grid,
  Button,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Card,
  CardContent,
  Avatar,
  Stack,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CheckCircle as ApprovedIcon,
  Cancel as RejectedIcon,
  Pending as PendingIcon,
  Schedule as ScheduleIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  CalendarToday as CalendarIcon,
  Category as CategoryIcon,
  Fingerprint as FingerprintIcon,
  Visibility as VisibilityIcon,
  Comment as RemarkIcon,
} from '@mui/icons-material';
import { dashboardApi } from '../services/api';
import RichTextEditor from './common/RichTextEditor';
import RichTextView from './common/RichTextView';
import ApprovalActions from './common/ApprovalActions';
import { useAuth } from '../contexts/AuthContext';
import { SEARCE_COLORS } from '../theme';

interface Proposal {
  id: string;
  name: string;
  email: string;
  manager_email: string;
  sbu: string;
  sbu_lead_email: string;
  asset_type: string;
  title: string;
  short_description: string;
  status: string;
  created_by: string;
  created_at: string;
  updated_at: string;
  problem_statement_question?: string;
  problem_impact_metrics?: string;
  target_audience_description?: string;
  solution_overview?: string;
  key_features?: string;
  hyperscaler_services?: string;
  known_dependencies?: string;
  diagrams_links?: string;
  gtm_category?: string;
  google_partnership_strategy?: string;
  specialized_skills_enhancement?: string;
  value_proposition?: string;
  resource_estimate?: string;
  required_skills?: string;
  known_risks?: string;
  critical_prerequisites?: string;
  detailed_description?: string;
  approval_history?: Array<{
    id?: string;
    level?: number;
    approval_level?: number;
    approval_level_name?: string;
    action: string;
    decision?: string;
    approver_email?: string;
    approver_name?: string;
    actor_email?: string;
    actor_name?: string;
    actor_role?: string;
    rejection_reason?: string;
    remarks?: string;
    timestamp?: string;
    action_timestamp?: string;
    user_email?: string;
    user_name?: string;
  }>;
  approval_actions?: Array<{
    id: string;
    action_type: string;
    approver_email: string;
    approver_name?: string;
    remarks?: string;
    created_at: string;
    updated_at: string;
  }>;
  last_edited_at?: string;
  last_edited_by?: string;
  edit_count?: number;
}

interface ProposalViewProps {
  viewMode?: 'creator' | 'approver' | 'viewer';
  showApprovalActions?: boolean;
}

interface TimelineStep {
  label: string;
  status: string;
  user: string;
  timestamp: string;
  description: string;
  icon: React.ReactElement;
  remarks?: string;
}

const ProposalView: React.FC<ProposalViewProps> = ({
  viewMode: propViewMode = 'viewer',
  showApprovalActions: propShowApprovalActions = false
}) => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();

  // Parse URL parameters for viewMode and showApprovalActions
  const urlParams = new URLSearchParams(window.location.search);
  const urlViewMode = urlParams.get('viewMode') as 'creator' | 'approver' | 'viewer' | null;
  const urlShowApprovalActions = urlParams.get('showApprovalActions') === 'true';

  const viewMode = urlViewMode || propViewMode;
  const showApprovalActions = urlShowApprovalActions || propShowApprovalActions;
  const [proposal, setProposal] = useState<Proposal | null>(null);
  const [loading, setLoading] = useState(true);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');
  const [approvalLoading, setApprovalLoading] = useState(false);
  const [approvalError, setApprovalError] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState<Partial<Proposal>>({});
  const [saving, setSaving] = useState(false);

  const fetchProposal = useCallback(async () => {
    try {
      setLoading(true);
      const response = await dashboardApi.getProposalById(id!);
      setProposal(response);
    } catch (error) {
      console.error('Error fetching proposal:', error);
      showSnackbar('Failed to load proposal', 'error');
      navigate('/internal-project-request');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    if (id) {
      fetchProposal();
    }
  }, [id, fetchProposal]);

  const showSnackbar = (message: string, severity: 'success' | 'error') => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };

  const handleDeleteProposal = () => {
    setDeleteModalOpen(true);
  };

  const handleCloseDeleteModal = () => {
    setDeleteModalOpen(false);
  };

  const handleConfirmDelete = async () => {
    if (!proposal || !id) return;

    try {
      await dashboardApi.deleteProposal(id);
      showSnackbar('Proposal deleted successfully!', 'success');
      navigate('/internal-project-request');
    } catch (error) {
      console.error('Error deleting proposal:', error);
      showSnackbar('Failed to delete proposal. Please try again.', 'error');
    }
  };

  const handleEditProposal = () => {
    if (!proposal) return;

    // Initialize edit data with current proposal data
    setEditData({
      title: proposal.title,
      short_description: proposal.short_description,
      problem_statement_question: proposal.problem_statement_question,
      problem_impact_metrics: proposal.problem_impact_metrics,
      target_audience_description: proposal.target_audience_description,
      solution_overview: proposal.solution_overview,
      key_features: proposal.key_features,
      hyperscaler_services: proposal.hyperscaler_services,
      known_dependencies: proposal.known_dependencies,
      diagrams_links: proposal.diagrams_links,
      gtm_category: proposal.gtm_category,
      google_partnership_strategy: proposal.google_partnership_strategy,
      specialized_skills_enhancement: proposal.specialized_skills_enhancement,
      value_proposition: proposal.value_proposition,
      resource_estimate: proposal.resource_estimate,
      required_skills: proposal.required_skills,
      known_risks: proposal.known_risks,
      critical_prerequisites: proposal.critical_prerequisites,
      detailed_description: proposal.detailed_description,
    });
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditData({});
  };

  const handleSaveEdit = async () => {
    if (!proposal || !id) return;

    try {
      setSaving(true);
      await dashboardApi.updateProposal(id, editData);

      // Refresh proposal data
      await fetchProposal();
      setIsEditing(false);
      setEditData({});
      showSnackbar('Proposal updated successfully!', 'success');
    } catch (error) {
      console.error('Error updating proposal:', error);
      showSnackbar('Failed to update proposal. Please try again.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleEditFieldChange = (field: string, value: string) => {
    setEditData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Approval action handlers
  const handleApprove = async (proposalId: string, remarks: string) => {
    setApprovalLoading(true);
    setApprovalError(null);
    try {
      await dashboardApi.approveProposal(proposalId, remarks);
      await fetchProposal(); // Refresh the proposal data
      showSnackbar('Proposal approved successfully!', 'success');
    } catch (error) {
      console.error('Error approving proposal:', error);
      setApprovalError('Failed to approve proposal');
      showSnackbar('Failed to approve proposal. Please try again.', 'error');
    } finally {
      setApprovalLoading(false);
    }
  };

  const handleReject = async (proposalId: string, remarks: string) => {
    setApprovalLoading(true);
    setApprovalError(null);
    try {
      await dashboardApi.rejectProposal(proposalId, remarks);
      await fetchProposal(); // Refresh the proposal data
      showSnackbar('Proposal rejected successfully!', 'success');
    } catch (error) {
      console.error('Error rejecting proposal:', error);
      setApprovalError('Failed to reject proposal');
      showSnackbar('Failed to reject proposal. Please try again.', 'error');
    } finally {
      setApprovalLoading(false);
    }
  };

  const handleAddRemark = async (proposalId: string, remarks: string) => {
    setApprovalLoading(true);
    setApprovalError(null);
    try {
      await dashboardApi.addRemark(proposalId, remarks);
      await fetchProposal(); // Refresh the proposal data
      showSnackbar('Remark added successfully!', 'success');
    } catch (error) {
      console.error('Error adding remark:', error);
      setApprovalError('Failed to add remark');
      showSnackbar('Failed to add remark. Please try again.', 'error');
    } finally {
      setApprovalLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const month = monthNames[date.getMonth()];
    const year = String(date.getFullYear()).slice(-2);
    
    // Format time as hh:mm AM/PM
    let hours = date.getHours();
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12;
    hours = hours ? hours : 12; // the hour '0' should be '12'
    const formattedTime = `${hours}:${minutes} ${ampm}`;
    
    return `${day}-${month}-${year}, ${formattedTime}`;
  };

  const canEditOrDelete = (status: string) => {
    return status === 'pending_manager_approval';
  };

  const canCurrentUserApprove = () => {
    if (!proposal || !user) return false;

    const currentUserEmail = user.email;
    const isPendingManagerApproval = proposal.status === 'pending_manager_approval';
    const isPendingSbuApproval = proposal.status === 'pending_sbu_approval';

    // Check if current user is the manager and approval is pending from manager
    const isCurrentUserManager = proposal.manager_email === currentUserEmail;
    const isCurrentUserSbuLead = proposal.sbu_lead_email === currentUserEmail;

    // Show approval actions only if:
    // 1. Status is pending_manager_approval AND current user is the manager, OR
    // 2. Status is pending_sbu_approval AND current user is the SBU lead
    return (isPendingManagerApproval && isCurrentUserManager) ||
      (isPendingSbuApproval && isCurrentUserSbuLead);
  };

  const renderApprovalTimeline = () => {
    if (!proposal) return null;

    const steps: TimelineStep[] = [
      {
        label: 'Proposal Created',
        status: 'completed',
        user: proposal.created_by,
        timestamp: proposal.created_at,
        description: 'Asset proposal submitted for review',
        icon: <PersonIcon />
      }
    ];

    // Add edit tracking step if proposal has been edited (before 1st level approval)
    if (proposal.last_edited_at) {
      steps.push({
        label: 'Last Edited',
        status: 'info',
        user: proposal.last_edited_by || 'Unknown',
        timestamp: proposal.last_edited_at,
        description: `Edited ${proposal.edit_count || 0} time(s)`,
        icon: <EditIcon />
      });
    }

    // Add approval actions from the database (if any) - but only for non-approval actions
    if (proposal.approval_actions && proposal.approval_actions.length > 0) {
      proposal.approval_actions.forEach((action) => {
        // Only show non-approval actions (remarks, edits) to avoid duplication
        if (action.action_type === 'remark' || action.action_type === 'edited') {
          let label = '';
          let status = 'info';
          let description = '';

          switch (action.action_type) {
            case 'remark':
              label = 'Remark Added';
              status = 'info';
              description = `Remark added by ${action.approver_name || action.approver_email}`;
              break;
            case 'edited':
              label = 'Proposal Edited';
              status = 'info';
              description = `Edited by ${action.approver_name || action.approver_email}`;
              break;
          }

          steps.push({
            label,
            status,
            user: action.approver_name || action.approver_email,
            timestamp: action.created_at,
            description: action.remarks ? `${description}` : description,
            remarks: action.remarks,
            icon: action.action_type === 'remark' ? <RemarkIcon /> : <EditIcon />
          });
        }
      });
    }

    // Find approval/rejection remarks from approval actions
    const getApprovalRemarks = (approverEmail: string, actionType: 'approve' | 'reject') => {
      if (!proposal.approval_actions) return undefined;

      const action = proposal.approval_actions.find(
        a => a.approver_email === approverEmail && a.action_type === actionType && a.remarks
      );
      return action ? action.remarks : undefined;
    };

    // Add the approval steps with proper status based on proposal status
    steps.push(
      {
        label: '1st Level Approval',
        status: proposal.status === 'pending_manager_approval' ? 'pending' :
          proposal.status === 'manager_rejected' ? 'rejected' :
            ['manager_approved', 'pending_sbu_approval', 'sbu_approved', 'sbu_rejected'].includes(proposal.status) ? 'completed' : 'completed',
        user: proposal.manager_email,
        timestamp: proposal.status === 'manager_rejected' ? proposal.updated_at || '' :
          ['manager_approved', 'pending_sbu_approval', 'sbu_approved', 'sbu_rejected'].includes(proposal.status) ? proposal.updated_at || '' : '',
        description: proposal.status === 'pending_manager_approval' ?
          'Awaiting approval from manager' :
          proposal.status === 'manager_rejected' ?
            'Rejected by manager' :
            'Approved by manager',
        remarks: proposal.status === 'manager_rejected' ?
          getApprovalRemarks(proposal.manager_email, 'reject') :
          ['manager_approved', 'pending_sbu_approval', 'sbu_approved', 'sbu_rejected'].includes(proposal.status) ?
            getApprovalRemarks(proposal.manager_email, 'approve') : undefined,
        icon: <PersonIcon />
      },
      {
        label: '2nd Level Approval',
        status: proposal.status === 'pending_sbu_approval' ? 'pending' :
          proposal.status === 'sbu_approved' ? 'completed' :
            proposal.status === 'sbu_rejected' ? 'rejected' : 'pending',
        user: proposal.sbu_lead_email,
        timestamp: proposal.status === 'sbu_rejected' ? proposal.updated_at || '' :
          proposal.status === 'sbu_approved' ? proposal.updated_at || '' : '',
        description: proposal.status === 'pending_sbu_approval' ?
          'Awaiting approval from SBU Lead' :
          proposal.status === 'sbu_approved' ?
            'Approved by SBU Lead' :
            proposal.status === 'sbu_rejected' ?
              'Rejected by SBU Lead' :
              'Pending 1st level approval',
        remarks: proposal.status === 'sbu_approved' ?
          getApprovalRemarks(proposal.sbu_lead_email, 'approve') :
          proposal.status === 'sbu_rejected' ?
            getApprovalRemarks(proposal.sbu_lead_email, 'reject') : undefined,
        icon: <PersonIcon />
      }
    );

    return (
      <Card sx={{
        borderRadius: 2,
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        border: '1px solid #e2e8f0',
        position: 'sticky',
        top: 100
      }}>
        <CardContent sx={{ p: 3 }}>
          <Typography variant="h6" sx={{
            mb: 3,
            color: SEARCE_COLORS.textPrimary,
            fontWeight: 600,
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }}>
            <ScheduleIcon sx={{ color: SEARCE_COLORS.primary }} />
            Approval Timeline
          </Typography>

          <Stack spacing={3}>
            {steps.map((step, index) => (
              <Box key={index} sx={{ position: 'relative' }}>
                {/* Connection Line */}
                {index < steps.length - 1 && (
                  <Box
                    sx={{
                      position: 'absolute',
                      left: 20,
                      top: 40,
                      width: '2px',
                      height: 'calc(100% + 12px)',
                      backgroundColor: step.status === 'completed' ? SEARCE_COLORS.success :
                        step.status === 'rejected' ? SEARCE_COLORS.error :
                          step.status === 'pending' ? SEARCE_COLORS.warning :
                            step.status === 'info' ? '#6366f1' :
                              '#e2e8f0',
                      zIndex: 1
                    }}
                  />
                )}

                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                  {/* Status Icon */}
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: step.status === 'completed' ? SEARCE_COLORS.success :
                        step.status === 'rejected' ? SEARCE_COLORS.error :
                          step.status === 'pending' ? SEARCE_COLORS.warning :
                            step.status === 'info' ? '#6366f1' :
                              '#e2e8f0',
                      color: 'white',
                      zIndex: 2,
                      position: 'relative'
                    }}
                  >
                    {step.status === 'completed' ? <ApprovedIcon sx={{ fontSize: 20 }} /> :
                      step.status === 'rejected' ? <RejectedIcon sx={{ fontSize: 20 }} /> :
                        step.status === 'pending' ? <ScheduleIcon sx={{ fontSize: 20 }} /> :
                          step.status === 'info' ? <RemarkIcon sx={{ fontSize: 20 }} /> :
                            <PendingIcon sx={{ fontSize: 20 }} />}
                  </Box>

                  {/* Content */}
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="subtitle2" sx={{
                      fontWeight: 600,
                      color: step.status === 'completed' ? SEARCE_COLORS.success :
                        step.status === 'rejected' ? SEARCE_COLORS.error :
                          step.status === 'pending' ? SEARCE_COLORS.warning :
                            step.status === 'info' ? '#6366f1' :
                              SEARCE_COLORS.textSecondary,
                      mb: 0.5
                    }}>
                      {step.label}
                    </Typography>

                    <Typography variant="body2" sx={{
                      color: SEARCE_COLORS.textSecondary,
                      mb: 1,
                      lineHeight: 1.4
                    }}>
                      {step.description}
                    </Typography>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                      <EmailIcon sx={{ fontSize: 14, color: SEARCE_COLORS.textSecondary }} />
                      <Typography variant="body2" sx={{
                        fontWeight: 500,
                        color: SEARCE_COLORS.textPrimary,
                        fontSize: '0.8rem'
                      }}>
                        {step.user}
                      </Typography>
                    </Box>

                    {step.timestamp && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: step.remarks ? 1 : 0 }}>
                        <CalendarIcon sx={{ fontSize: 14, color: SEARCE_COLORS.textSecondary }} />
                        <Typography variant="body2" sx={{
                          color: SEARCE_COLORS.textSecondary,
                          fontSize: '0.75rem'
                        }}>
                          {formatDate(step.timestamp)}
                        </Typography>
                      </Box>
                    )}

                    {/* Remarks Display */}
                    {step.remarks && (
                      <Box sx={{
                        mt: 1,
                        p: 2,
                        backgroundColor: step.status === 'rejected' ? '#fef2f2' :
                          step.status === 'info' ? '#f8fafc' : '#f0f9ff',
                        border: `1px solid ${step.status === 'rejected' ? '#fecaca' :
                          step.status === 'info' ? '#e2e8f0' : '#bae6fd'}`,
                        borderRadius: 2,
                        borderLeft: `4px solid ${step.status === 'rejected' ? SEARCE_COLORS.error :
                          step.status === 'info' ? '#6366f1' : SEARCE_COLORS.primary}`
                      }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          <RemarkIcon sx={{
                            fontSize: 16, color: step.status === 'rejected' ? SEARCE_COLORS.error :
                              step.status === 'info' ? '#6366f1' : SEARCE_COLORS.primary
                          }} />
                          <Typography variant="body2" sx={{
                            fontWeight: 600,
                            color: step.status === 'rejected' ? SEARCE_COLORS.error :
                              step.status === 'info' ? '#6366f1' : SEARCE_COLORS.primary,
                            fontSize: '0.8rem'
                          }}>
                            {step.status === 'rejected' ? 'Rejection Reason' :
                              step.status === 'info' ? 'Note' : 'Remarks'}
                          </Typography>
                        </Box>
                        <Typography variant="body2" sx={{
                          color: SEARCE_COLORS.textPrimary,
                          lineHeight: 1.5,
                          fontSize: '0.85rem'
                        }}>
                          {step.remarks}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Box>
              </Box>
            ))}
          </Stack>
        </CardContent>
      </Card>
    );
  };

  const renderRichTextContent = (content: string, title: string, fieldName?: string) => {
    if (!content && !isEditing) return null;

    return (
      <Box sx={{ mb: 4 }}>
        <Typography variant="subtitle1" sx={{
          fontWeight: 600,
          color: SEARCE_COLORS.textPrimary,
          mb: 2
        }}>
          {title}
        </Typography>
        {isEditing && fieldName ? (
          <RichTextEditor
            value={String(editData[fieldName as keyof typeof editData] || '')}
            onChange={(value) => handleEditFieldChange(fieldName, value)}
            placeholder={`Enter ${title.toLowerCase()}...`}
            minHeight={100}
          />
        ) : (
          <Box sx={{ backgroundColor: '#f8fafc', borderRadius: 1, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
            <RichTextView html={content} />
          </Box>
        )}
      </Box>
    );
  };

  if (loading) {
    return <DashboardSectionSkeleton />;
  }

  if (!proposal) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="h6" color="textSecondary">
          Proposal not found
        </Typography>
        <Button
          variant="contained"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/requests')}
          sx={{ mt: 2 }}
        >
          Back to Proposals
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#f8fafc' }}>
      {/* Hero Header - Clean & Minimal */}
      <Box sx={{
        backgroundColor: 'white',
        borderBottom: '1px solid #e2e8f0',
        position: 'sticky',
        top: 0,
        zIndex: 100,
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
      }}>
        <Box sx={{ maxWidth: '1400px', mx: 'auto', px: 3, py: 3 }}>
          <Box sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 3
          }}>
            {/* Left Side - Back Button */}
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate('/requests')}
              sx={{
                color: SEARCE_COLORS.textSecondary,
                textTransform: 'none',
                fontWeight: 500,
                px: 2,
                py: 1,
                borderRadius: 2,
                '&:hover': {
                  backgroundColor: '#f8fafc',
                  color: SEARCE_COLORS.primary
                }
              }}
            >
              Back to Proposals
            </Button>

            {/* Center - Title */}
            <Box sx={{ flex: 1, textAlign: 'center', minWidth: 0 }}>
              <Typography variant="h3" sx={{
                fontWeight: 700,
                color: SEARCE_COLORS.textPrimary,
                lineHeight: 1.2,
                fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' }
              }}>
                {proposal.title}
              </Typography>
            </Box>

            {/* Right Side - Action Buttons */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {/* Show Edit/Delete buttons for creators */}
              {viewMode === 'creator' && canEditOrDelete(proposal.status) && (
                <>
                  <Button
                    variant="outlined"
                    startIcon={<EditIcon />}
                    onClick={handleEditProposal}
                    size="small"
                    sx={{
                      borderColor: SEARCE_COLORS.primary,
                      color: SEARCE_COLORS.primary,
                      textTransform: 'none',
                      fontWeight: 600,
                      px: 2,
                      py: 1,
                      borderRadius: 2,
                      '&:hover': {
                        borderColor: SEARCE_COLORS.primary,
                        backgroundColor: `${SEARCE_COLORS.primary}10`,
                      },
                    }}
                  >
                    Edit
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<DeleteIcon />}
                    onClick={handleDeleteProposal}
                    size="small"
                    sx={{
                      borderColor: SEARCE_COLORS.error,
                      color: SEARCE_COLORS.error,
                      textTransform: 'none',
                      fontWeight: 600,
                      px: 2,
                      py: 1,
                      borderRadius: 2,
                      '&:hover': {
                        borderColor: SEARCE_COLORS.error,
                        backgroundColor: `${SEARCE_COLORS.error}10`,
                      },
                    }}
                  >
                    Delete
                  </Button>
                </>
              )}

              {/* Show approval actions for approvers */}
              {viewMode === 'approver' && showApprovalActions && (
                <Typography variant="body2" sx={{ color: SEARCE_COLORS.textSecondary }}>
                  Approval actions available in the sidebar
                </Typography>
              )}
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Main Content */}
      <Box sx={{ maxWidth: '1400px', mx: 'auto', p: 3 }}>
        <Grid container spacing={3}>
          {/* Left Column - Proposal Content */}
          <Grid item xs={12} lg={8}>
            <Stack spacing={3}>
              {/* General Information Card */}
              <Card sx={{
                borderRadius: 2,
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                border: '1px solid #e2e8f0'
              }}>
                <CardContent sx={{ p: 3 }}>
                  <Typography variant="h6" sx={{
                    mb: 3,
                    color: SEARCE_COLORS.textPrimary,
                    fontWeight: 600,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1
                  }}>
                    <PersonIcon sx={{ color: SEARCE_COLORS.primary }} />
                    General Information
                  </Typography>

                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                        <Avatar sx={{
                          bgcolor: SEARCE_COLORS.primary,
                          width: 40,
                          height: 40,
                          fontSize: '0.875rem'
                        }}>
                          {proposal.created_by.charAt(0).toUpperCase()}
                        </Avatar>
                        <Box>
                          <Typography variant="body2" sx={{
                            color: SEARCE_COLORS.textSecondary,
                            fontWeight: 500,
                            mb: 0.5
                          }}>
                            Initiated by
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 600 }}>
                            {proposal.created_by}
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                        <CalendarIcon sx={{ color: SEARCE_COLORS.primary, fontSize: 24 }} />
                        <Box>
                          <Typography variant="body2" sx={{
                            color: SEARCE_COLORS.textSecondary,
                            fontWeight: 500,
                            mb: 0.5
                          }}>
                            Created at
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 600 }}>
                            {formatDate(proposal.created_at)}
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                        <CategoryIcon sx={{ color: SEARCE_COLORS.primary, fontSize: 24 }} />
                        <Box>
                          <Typography variant="body2" sx={{
                            color: SEARCE_COLORS.textSecondary,
                            fontWeight: 500,
                            mb: 0.5
                          }}>
                            Asset Type
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 600 }}>
                            {proposal.asset_type}
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                        <FingerprintIcon sx={{ color: SEARCE_COLORS.primary, fontSize: 24 }} />
                        <Box>
                          <Typography variant="body2" sx={{
                            color: SEARCE_COLORS.textSecondary,
                            fontWeight: 500,
                            mb: 0.5
                          }}>
                            Proposal ID
                          </Typography>
                          <Typography variant="body1" sx={{
                            fontWeight: 600,
                            fontFamily: 'monospace',
                            fontSize: '0.875rem',
                            color: SEARCE_COLORS.textSecondary
                          }}>
                            {proposal.id}
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              {/* Proposal Content */}
              <Card sx={{
                borderRadius: 2,
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                border: '1px solid #e2e8f0'
              }}>
                <CardContent sx={{ p: 3 }}>
                  <Typography variant="h6" sx={{
                    mb: 3,
                    color: SEARCE_COLORS.textPrimary,
                    fontWeight: 600,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1
                  }}>
                    <VisibilityIcon sx={{ color: SEARCE_COLORS.primary }} />
                    Proposal Content
                  </Typography>

                  {/* Short Description */}
                  <Box sx={{ mb: 4 }}>
                    <Typography variant="subtitle1" sx={{
                      fontWeight: 600,
                      color: SEARCE_COLORS.textPrimary,
                      mb: 2
                    }}>
                      Short Description
                    </Typography>
                    {isEditing ? (
                      <RichTextEditor
                        value={editData.short_description || ''}
                        onChange={(value) => handleEditFieldChange('short_description', value)}
                        placeholder="Provide a brief description of the asset..."
                        minHeight={100}
                      />
                    ) : (
                      <Box sx={{ backgroundColor: '#f8fafc', borderRadius: 1, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
                        <RichTextView html={proposal.short_description} />
                      </Box>
                    )}
                  </Box>

                  {/* Conditional Content based on Asset Type */}
                  {(proposal.asset_type === 'Accelerator' || proposal.asset_type === 'IP') && (
                    <>
                      {renderRichTextContent(proposal.problem_statement_question || '', 'Problem Statement', 'problem_statement_question')}
                      {renderRichTextContent(proposal.solution_overview || '', 'Solution Overview', 'solution_overview')}
                      {renderRichTextContent(proposal.key_features || '', 'Key Features', 'key_features')}
                    </>
                  )}

                  {/* Detailed Description for POC/Project */}
                  {['Client POC', 'Internal POC', 'Internal Project'].includes(proposal.asset_type) &&
                    renderRichTextContent(proposal.detailed_description || '', 'Detailed Description', 'detailed_description')}

                  {/* Common Fields */}
                  {renderRichTextContent(proposal.value_proposition || '', 'Value Proposition', 'value_proposition')}
                  {renderRichTextContent(proposal.resource_estimate || '', 'Resource Estimate', 'resource_estimate')}

                  {/* Edit Actions */}
                  {isEditing && (
                    <Box sx={{
                      display: 'flex',
                      justifyContent: 'flex-end',
                      gap: 2,
                      mt: 3,
                      pt: 3,
                      borderTop: '1px solid #e2e8f0'
                    }}>
                      <Button
                        variant="outlined"
                        onClick={handleCancelEdit}
                        disabled={saving}
                        sx={{
                          borderColor: SEARCE_COLORS.textSecondary,
                          color: SEARCE_COLORS.textSecondary,
                          textTransform: 'none',
                          fontWeight: 600,
                          px: 3,
                          py: 1,
                          borderRadius: 2,
                          '&:hover': {
                            borderColor: SEARCE_COLORS.textSecondary,
                            backgroundColor: '#f8fafc',
                          },
                        }}
                      >
                        Cancel
                      </Button>
                      <Button
                        variant="contained"
                        onClick={handleSaveEdit}
                        disabled={saving}
                        startIcon={saving ? <InlineSpinner size={16} thickness={5} /> : null}
                        sx={{
                          backgroundColor: SEARCE_COLORS.primary,
                          textTransform: 'none',
                          fontWeight: 600,
                          px: 3,
                          py: 1,
                          borderRadius: 2,
                          '&:hover': {
                            backgroundColor: SEARCE_COLORS.primary,
                            opacity: 0.9,
                          },
                        }}
                      >
                        {saving ? 'Saving...' : 'Save Changes'}
                      </Button>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Stack>
          </Grid>

          {/* Right Column - Approval Timeline & Actions */}
          <Grid item xs={12} lg={4}>
            <Stack spacing={3}>
              {/* Approval Actions for approvers */}
              {viewMode === 'approver' && showApprovalActions && canCurrentUserApprove() && (
                <Card sx={{
                  border: `1px solid ${SEARCE_COLORS.primary}20`,
                  borderRadius: 3,
                  overflow: 'hidden'
                }}>
                  <ApprovalActions
                    proposalId={proposal.id}
                    onApprove={handleApprove}
                    onReject={handleReject}
                    onAddRemark={handleAddRemark}
                    loading={approvalLoading}
                    error={approvalError}
                  />
                </Card>
              )}

              {/* Approval Timeline */}
              {renderApprovalTimeline()}
            </Stack>
          </Grid>
        </Grid>
      </Box>

      {/* Delete Confirmation Modal */}
      <Dialog open={deleteModalOpen} onClose={handleCloseDeleteModal}>
        <DialogTitle>
          <Typography variant="h6" sx={{ color: SEARCE_COLORS.textPrimary }}>
            Delete Asset Proposal
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Are you sure you want to delete this asset proposal?
          </Typography>
          <Box sx={{ p: 2, backgroundColor: '#f8fafc', borderRadius: 1, mb: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: SEARCE_COLORS.textPrimary }}>
              {proposal.title}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {proposal.asset_type} • Created {formatDate(proposal.created_at)}
            </Typography>
          </Box>
          <Typography variant="body2" color="error">
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteModal}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirmDelete}
            startIcon={<DeleteIcon />}
          >
            Delete Proposal
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbarSeverity}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ProposalView;
